import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { catchError, throwError } from 'rxjs';
import { CommonService } from '@services/common/common.service';

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const commonService = inject(CommonService);
  let newReq;

  // Skip if Authorization header is already present.
  if (req.headers.has('Authorization') || req.headers.has('skip')) {
    newReq = req.clone();
  } else {
    // Get authentication service
    const authService = inject(AuthService);

    // Get access token
    const accessToken = authService.getAccessToken();

    // Clone the request to add the authentication header.
    newReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
    });
  }

  return next(newReq).pipe(
    catchError((error: any) => {
      // HttpErrorResponse
      const status = error.status;
      let body = error.error; // ProblemDetails or string

      // If the body is a string, try to parse it as JSON. If it fails, leave it as-is.
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body);
        } catch {
          body = {};
        }
      }

      // ProblemDetails fields and extensions
      const title = body?.title;
      const detail = body?.detail;
      const extensions = body?.extensions ?? body; // if you wrote extensions flat
      const userMessage = extensions?.userMessage as string | undefined;
      const errorCode = extensions?.errorCode as string | undefined;
      const correlationId = extensions?.correlationId as string | undefined;
      const referenceId = extensions?.referenceId as string | undefined;
      const isAuthenticated = extensions?.isAuthenticated as
        | boolean
        | undefined;

      // Choose the best message to display
      const message =
        userMessage ?? detail ?? title ?? error.message ?? 'Unexpected Error';

      // Append support code if available
      const supportCode = referenceId ? ` (${referenceId})` : '';

      // Special case: status=0 => network/CORS/SSL
      if (status === 0) {
        console.error('Network/CORS error:', error);
      } else {
        console.error(`HTTP ${status}:`, body ?? error);
      }

      if (status === 0) {
        // Network/CORS/SSL issue
        commonService.errorNotification(
          'Network error. Please check your connection and try again.',
          'center',
          'bottom',
          2500,
        );
      } else if (status === 401) {
        // Handle auth as you already do (redirect or notify)
        commonService.errorNotification(
          'Your session has expired. Please sign in again.',
          'center',
          'bottom',
          2000,
        );
      } else if (status === 403) {
        commonService.errorNotification(
          'You do not have permission to perform this action.',
          'center',
          'bottom',
          2000,
        );
      } else if (status === 404) {
        commonService.errorNotification(
          'The requested resource was not found.',
          'center',
          'bottom',
          2000,
        );
      } else {
        // 4xx/5xx: show friendly message (with correlation id if present)
        commonService.errorNotification(
          `${message}${supportCode}`,
          'center',
          'bottom',
          2500,
        );
      }

      // Prefer re-throwing a normalized Error so components can optionally react
      return throwError(() => new Error(message));

      // if (error.error instanceof Error) {
      //   // A client-side or network error occurred. Handle it accordingly.
      //   commonService.errorNotification(error.error.message, "center", "bottom", 1200);
      // } else if (error.error != null) {
      //   // The backend returned an unsuccessful response code.
      //   // The response body may contain clues as to what went wrong,
      //   commonService.errorNotification(`${error.error.title}, ${error.error.detail}`, "center", "bottom", 1200);
      // } else {
      //   //commonService.errorNotification(`${error.error.title}, ${error.error.detail}`, "center", "bottom", 1200);
      //   // If you want to return the error on the upper level:
      //   //return throwError(error);
      //   return throwError(() => error);
      // }

      // // If you want to return a new response:
      // //return of(new HttpResponse({body: [{name: "Default value..."}]}));

      // // or just return nothing:
      // return EMPTY;

      // Optionally re-throw the error to propagate it further
      //return throwError(() => error);
    }),
  );
};
